import { endReducer } from '@/utils/end-reducer';
import { ItemListOption } from '@/contexts/item-context-types';
import { useCallback, useReducer, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

export type DispatchActionFn = (
  dispatch: Dispatch,
  getState: () => State,
) => void;

export type Dispatch = (action: Action | DispatchActionFn) => void;

export interface Item {
  isMinimized: boolean;
  itemListOption: ItemListOption;
  itemUUID: string;
  notes: string | null;
  parLevel: number | null;
  persistNotes: boolean;
}

export interface Partition {
  isDefault: boolean;
  items: Item[];
  partitionName: string | null;
  partitionUUID: string;
}

export interface State {
  collectionName: string | null;
  collectionUUID: string | null;
  partitions: Partition[];
  selectedPartitionUUID: string | null;
}

export enum EditOrderGuideActions {
  ADD_ITEM = 'ADD_ITEM',
  ADD_PARTITION = 'ADD_PARTITION',
  DUPLICATE_ITEMS = 'DUPLICATE_ITEMS',
  HYDRATE = 'HYDRATE',
  MOVE_ITEMS = 'MOVE_ITEMS',
  MOVE_ITEMS_TO_BOTTOM = 'MOVE_ITEMS_TO_BOTTOM',
  MOVE_ITEMS_TO_TOP = 'MOVE_ITEMS_TO_TOP',
  REMOVE_ITEMS = 'REMOVE_ITEMS',
  REMOVE_PARTITION = 'REMOVE_PARTITION',
  SET_COLLECTION_NAME = 'SET_COLLECTION_NAME',
  SET_ITEM_PAR_LEVEL = 'SET_ITEM_PAR_LEVEL',
  SET_PARTITION_NAME = 'SET_PARTITION_NAME',
  SET_SELECTED_PARTITION = 'SET_SELECTED_PARTITION',
  SORT_ITEMS = 'SORT_ITEMS',
  SORT_PARTITIONS = 'SORT_PARTITIONS',
}

export type Action =
  | {
      payload: {
        itemListOption: ItemListOption;
        partitionUUID: string;
      };
      type: EditOrderGuideActions.ADD_ITEM;
    }
  | {
      payload: {
        partitionName: string;
        stayOnSelectedPartition?: boolean;
      };
      type: EditOrderGuideActions.ADD_PARTITION;
    }
  | {
      payload: {
        itemUUIDs: string[];
        sourcePartitionUUID: string;
        targetPartitionUUID: string;
      };
      type: EditOrderGuideActions.DUPLICATE_ITEMS;
    }
  | {
      payload: { hydratedState: State };
      type: EditOrderGuideActions.HYDRATE;
    }
  | {
      payload: {
        itemUUIDs: string[];
        sourcePartitionUUID: string;
        targetPartitionUUID: string;
      };
      type: EditOrderGuideActions.MOVE_ITEMS;
    }
  | {
      payload: {
        itemUUIDs: string[];
        partitionUUID: string;
      };
      type: EditOrderGuideActions.MOVE_ITEMS_TO_BOTTOM;
    }
  | {
      payload: {
        itemUUIDs: string[];
        partitionUUID: string;
      };
      type: EditOrderGuideActions.MOVE_ITEMS_TO_TOP;
    }
  | {
      payload: { itemUUIDs: string[]; partitionUUID: string };
      type: EditOrderGuideActions.REMOVE_ITEMS;
    }
  | {
      payload: { partitionUUID: string };
      type: EditOrderGuideActions.REMOVE_PARTITION;
    }
  | {
      payload: { collectionName: string };
      type: EditOrderGuideActions.SET_COLLECTION_NAME;
    }
  | {
      payload: {
        itemUUID: string;
        parLevel: number | null;
        partitionUUID: string;
      };
      type: EditOrderGuideActions.SET_ITEM_PAR_LEVEL;
    }
  | {
      payload: { partitionName: string; partitionUUID: string };
      type: EditOrderGuideActions.SET_PARTITION_NAME;
    }
  | {
      payload: { partitionUUID: string };
      type: EditOrderGuideActions.SET_SELECTED_PARTITION;
    }
  | {
      payload: { partitionUUID: string; sortedItemUUIDs: string[] };
      type: EditOrderGuideActions.SORT_ITEMS;
    }
  | {
      payload: { sortedPartitionUUIDs: string[] };
      type: EditOrderGuideActions.SORT_PARTITIONS;
    };

const emptyInitialState: State = {
  collectionName: null,
  collectionUUID: null,
  partitions: [],
  selectedPartitionUUID: null,
};

const pushDefaultPartitionToEnd = (state: State): State => {
  return {
    ...state,
    partitions: state.partitions.sort((a, b) => {
      if (a.isDefault) {
        return 1;
      }
      if (b.isDefault) {
        return -1;
      }
      return 0;
    }),
  };
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case EditOrderGuideActions.ADD_ITEM:
      return {
        ...state,
        partitions: state.partitions.map((p) => {
          return p.partitionUUID === action.payload.partitionUUID
            ? {
                ...p,
                items: [
                  ...p.items,
                  {
                    isMinimized: false,
                    itemListOption: action.payload.itemListOption,
                    itemUUID: uuidv4(),
                    notes: null,
                    parLevel: null,
                    persistNotes: false,
                  },
                ],
              }
            : p;
        }),
      };
    case EditOrderGuideActions.ADD_PARTITION: {
      const newPartitionUUID = uuidv4();
      return pushDefaultPartitionToEnd({
        ...state,
        partitions: [
          ...state.partitions,
          {
            isDefault: false,
            items: [],
            partitionName: action.payload.partitionName,
            partitionUUID: newPartitionUUID,
          },
        ],
        selectedPartitionUUID: action.payload.stayOnSelectedPartition
          ? state.selectedPartitionUUID
          : newPartitionUUID,
      });
    }
    case EditOrderGuideActions.DUPLICATE_ITEMS: {
      const sourcePartition = state.partitions.find(
        (p) => p.partitionUUID === action.payload.sourcePartitionUUID,
      );
      if (!sourcePartition) {
        return state;
      }

      const itemUUIDSet = new Set(action.payload.itemUUIDs);
      return {
        ...state,
        partitions: state.partitions.map((p) => {
          return p.partitionUUID === action.payload.targetPartitionUUID
            ? {
                ...p,
                items: [
                  ...p.items,
                  ...sourcePartition.items
                    .filter((i) => itemUUIDSet.has(i.itemUUID))
                    .map((i) => ({
                      ...i,
                      isMinimized: false,
                      itemUUID: uuidv4(),
                      notes: null,
                    })),
                ],
              }
            : p;
        }),
      };
    }
    case EditOrderGuideActions.HYDRATE:
      return pushDefaultPartitionToEnd(action.payload.hydratedState);
    case EditOrderGuideActions.MOVE_ITEMS: {
      const sourcePartition = state.partitions.find(
        (p) => p.partitionUUID === action.payload.sourcePartitionUUID,
      );
      if (!sourcePartition) {
        return state;
      }

      const itemUUIDSet = new Set(action.payload.itemUUIDs);
      return {
        ...state,
        partitions: state.partitions.map((p) => {
          if (p.partitionUUID === action.payload.sourcePartitionUUID) {
            return {
              ...p,
              items: p.items.filter((i) => !itemUUIDSet.has(i.itemUUID)),
            };
          }

          if (p.partitionUUID === action.payload.targetPartitionUUID) {
            return {
              ...p,
              items: [
                ...p.items,
                ...sourcePartition.items
                  .filter((i) => itemUUIDSet.has(i.itemUUID))
                  .map((i) => ({
                    ...i,
                    isMinimized: false,
                  })),
              ],
            };
          }

          return p;
        }),
      };
    }
    case EditOrderGuideActions.MOVE_ITEMS_TO_BOTTOM: {
      const itemUUIDSet = new Set(action.payload.itemUUIDs);
      return {
        ...state,
        partitions: state.partitions.map((p) => {
          return p.partitionUUID === action.payload.partitionUUID
            ? {
                ...p,
                items: [
                  ...p.items.filter((i) => !itemUUIDSet.has(i.itemUUID)),
                  ...p.items.filter((i) => itemUUIDSet.has(i.itemUUID)),
                ],
              }
            : p;
        }),
      };
    }
    case EditOrderGuideActions.MOVE_ITEMS_TO_TOP: {
      const itemUUIDSet = new Set(action.payload.itemUUIDs);
      return {
        ...state,
        partitions: state.partitions.map((p) => {
          return p.partitionUUID === action.payload.partitionUUID
            ? {
                ...p,
                items: [
                  ...p.items.filter((i) => itemUUIDSet.has(i.itemUUID)),
                  ...p.items.filter((i) => !itemUUIDSet.has(i.itemUUID)),
                ],
              }
            : p;
        }),
      };
    }
    case EditOrderGuideActions.REMOVE_ITEMS: {
      const itemUUIDSet = new Set(action.payload.itemUUIDs);
      return {
        ...state,
        partitions: state.partitions.map((p) => {
          return p.partitionUUID === action.payload.partitionUUID
            ? {
                ...p,
                items: p.items.filter((i) => !itemUUIDSet.has(i.itemUUID)),
              }
            : p;
        }),
      };
    }
    case EditOrderGuideActions.REMOVE_PARTITION: {
      const nextPartitions = state.partitions.filter(
        (p) => p.partitionUUID !== action.payload.partitionUUID,
      );

      let nextSelectedPartitionUUID = state.selectedPartitionUUID;
      if (nextSelectedPartitionUUID === action.payload.partitionUUID) {
        nextSelectedPartitionUUID = nextPartitions[0]?.partitionUUID || null;
      }

      return {
        ...state,
        partitions: nextPartitions,
        selectedPartitionUUID: nextSelectedPartitionUUID,
      };
    }
    case EditOrderGuideActions.SET_COLLECTION_NAME:
      return {
        ...state,
        collectionName: action.payload.collectionName,
      };
    case EditOrderGuideActions.SET_ITEM_PAR_LEVEL:
      return {
        ...state,
        partitions: state.partitions.map((p) => {
          return p.partitionUUID === action.payload.partitionUUID
            ? {
                ...p,
                items: p.items.map((i) => {
                  return i.itemUUID === action.payload.itemUUID
                    ? {
                        ...i,
                        isMinimized: false,
                        parLevel: action.payload.parLevel,
                      }
                    : i;
                }),
              }
            : p;
        }),
      };
    case EditOrderGuideActions.SET_PARTITION_NAME:
      return {
        ...state,
        partitions: state.partitions.map((p) => {
          return p.partitionUUID === action.payload.partitionUUID
            ? { ...p, partitionName: action.payload.partitionName }
            : p;
        }),
      };
    case EditOrderGuideActions.SET_SELECTED_PARTITION: {
      return {
        ...state,
        selectedPartitionUUID: action.payload.partitionUUID,
      };
    }
    case EditOrderGuideActions.SORT_ITEMS: {
      return {
        ...state,
        partitions: state.partitions.map((p) => {
          if (p.partitionUUID === action.payload.partitionUUID) {
            // Use a map to maintain insertion order
            const itemMap = p.items.reduce((result, item) => {
              result.set(item.itemUUID, item);
              return result;
            }, new Map<string, Item>());

            const sortedItems = action.payload.sortedItemUUIDs.reduce<Item[]>(
              (result, itemUUID) => {
                const item = itemMap.get(itemUUID);
                if (item) {
                  result.push(item);
                  itemMap.delete(itemUUID);
                }

                return result;
              },
              [],
            );

            return {
              ...p,
              // Append any items that were not included in sortedItemUUIDs
              items: [...sortedItems, ...itemMap.values()],
            };
          }

          return p;
        }),
      };
    }
    case EditOrderGuideActions.SORT_PARTITIONS: {
      // Use a map to maintain insertion order
      const partitionMap = state.partitions.reduce((result, partition) => {
        result.set(partition.partitionUUID, partition);
        return result;
      }, new Map<string, Partition>());

      const sortedPartitions = action.payload.sortedPartitionUUIDs.reduce<
        Partition[]
      >((result, partitionUUID) => {
        const partition = partitionMap.get(partitionUUID);
        if (partition) {
          result.push(partition);
          partitionMap.delete(partitionUUID);
        }

        return result;
      }, []);

      return pushDefaultPartitionToEnd({
        ...state,
        // Append any partitions that were not included in sortedPartitionUUIDs
        partitions: [...sortedPartitions, ...partitionMap.values()],
      });
    }
    default:
      return endReducer(state, action);
  }
};

export const useEditOrderGuideReducer = (
  initialState: State = emptyInitialState,
): [State, Dispatch] => {
  const [state, baseDispatch] = useReducer(reducer, initialState);

  // Store and update state in a ref so actions can access the current state
  const stateRef = useRef(state);
  stateRef.current = state;

  const dispatch: Dispatch = useCallback((action) => {
    if (typeof action === 'function') {
      action(dispatch, () => stateRef.current);
    } else {
      baseDispatch(action);
    }
  }, []);

  return [state, dispatch];
};

export const validateExistingItems = (
  state: State,
  sourceItemUUIDs: string[],
  sourcePartitionUUID: string,
  targetPartitionUUID: string,
): [string[], Item[]] => {
  const sourcePartition = state.partitions.find(
    (p) => p.partitionUUID === sourcePartitionUUID,
  );
  const targetPartition = state.partitions.find(
    (p) => p.partitionUUID === targetPartitionUUID,
  );
  if (!sourcePartition || !targetPartition) {
    throw new Error('Source and target partitions not found');
  }

  const sourceItemMap = sourcePartition.items.reduce<{
    [itemUUID: string]: Item;
  }>((result, i) => {
    result[i.itemUUID] = i;
    return result;
  }, {});

  const targetVariantPackUUIDSet = new Set(
    targetPartition.items.map(
      (i) => i.itemListOption.metadata!.variantPackUUID,
    ),
  );

  const existingItems: Item[] = [];
  const itemUUIDs = sourceItemUUIDs.filter((itemUUID) => {
    const sourceItem = sourceItemMap[itemUUID];
    if (
      targetVariantPackUUIDSet.has(
        sourceItem.itemListOption.metadata!.variantPackUUID,
      )
    ) {
      existingItems.push(sourceItem);
      return false;
    }

    return true;
  });

  return [itemUUIDs, existingItems];
};
